import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * DateRange filter for data tables.
 *
 * @param {string} name - Base filter name used to derive query param keys.
 * @param {function} onSelect - Called when either date changes.
 * @param {Object} value - Current values object for controlled usage.
 * @param {string} startParam - Optional explicit start-date parameter name.
 * @param {string} endParam - Optional explicit end-date parameter name.
 * @param {string} startLabelKey - i18n key under searchFilters for start label.
 * @param {string} endLabelKey - i18n key under searchFilters for end label.
 */
const DateRange = ({
	name,
	onSelect,
	value = {},
	startParam,
	endParam,
	startLabelKey = "startDate",
	endLabelKey = "endDate",
}) => {
	const { t } = useTranslation();

	const params = useMemo(() => {
		const safeBase = name || "date";

		return {
			start: startParam || `${safeBase}_start`,
			end: endParam || `${safeBase}_end`,
		};
	}, [endParam, name, startParam]);

	const startValue =
		value?.[params.start] ?? value?.startDate ?? value?.start_date ?? "";
	const endValue = value?.[params.end] ?? value?.endDate ?? value?.end_date ?? "";

	const emit = (nextStart, nextEnd) => {
		if (!onSelect) return;

		onSelect({
			name: name || "date",
			startParam: params.start,
			endParam: params.end,
			startDate: nextStart,
			endDate: nextEnd,
			values: {
				[params.start]: nextStart,
				[params.end]: nextEnd,
			},
		});
	};

	return (
		<>
			<label>
				{t(`searchFilters.${startLabelKey}`)}{" "}
				<input
					type="date"
					name={params.start}
					className="text-input-sm"
					value={startValue}
					onChange={(e) => emit(e.target.value, endValue)}
				/>
			</label>

			<label>
				{t(`searchFilters.${endLabelKey}`)}{" "}
				<input
					type="date"
					name={params.end}
					className="text-input-sm"
					value={endValue}
					onChange={(e) => emit(startValue, e.target.value)}
				/>
			</label>
		</>
	);
};

export default DateRange;

